import { Component } from 'react';
import { withRouter } from 'react-router-dom';

const SITE_NAME = 'Jenkins Iron & Steel';
const DEFAULT_TITLE = 'Jenkins Iron & Steel Skid Steer Attachments';
const DEFAULT_DESCRIPTION = 'Shop American-made, heavy-duty skid steer attachments built for durability, performance, and value.';
const DEFAULT_KEYWORDS = 'skid steer attachments, american made attachments, jenkins iron and steel';
const DEFAULT_SOCIAL_IMAGE = 'https://jenkinsironandsteel.com/logo512.png';

const SEO_ROUTES = [
    {
        match: (path) => path === '/',
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        keywords: DEFAULT_KEYWORDS
    },
    {
        match: (path) => path.startsWith('/about-us'),
        title: `About Us | ${SITE_NAME}`,
        description: 'Learn about the Jenkins family, our manufacturing values, and our commitment to high quality skid steer attachments.',
        keywords: 'about jenkins iron and steel, skid steer attachment manufacturer'
    },
    {
        match: (path) => path.startsWith('/contact'),
        title: `Contact Us | ${SITE_NAME}`,
        description: 'Contact Jenkins Iron & Steel for product questions, support, and quote requests for skid steer attachments.',
        keywords: 'contact jenkins iron and steel, skid steer attachment support'
    },
    {
        match: (path) => path.startsWith('/categories'),
        title: `Skid Steer Attachment Categories | ${SITE_NAME}`,
        description: 'Browse our full catalog of commercial-grade skid steer attachments by category.',
        keywords: 'skid steer attachment categories, heavy duty attachments'
    },
    {
        match: (path) => path.startsWith('/products'),
        title: `Skid Steer Attachments | ${SITE_NAME}`,
        description: 'Explore product details, specifications, and options for Jenkins Iron & Steel skid steer attachments.',
        keywords: 'skid steer attachment products, jenkins product catalog',
        ogType: 'product'
    },
    {
        match: (path) => path.startsWith('/cart'),
        title: `Quote Cart | ${SITE_NAME}`,
        description: 'Review your selected attachments and submit your quote request.',
        keywords: 'quote cart, skid steer quote request',
        robots: 'noindex, nofollow'
    },
    {
        match: (path) => path.startsWith('/login') || path.startsWith('/register') || path.startsWith('/lostpassword') || path.startsWith('/resetpassword') || path.startsWith('/confirmuser') || path.startsWith('/my-account') || path.startsWith('/account-comfirmed'),
        title: `Account | ${SITE_NAME}`,
        description: 'Access and manage your Jenkins Iron & Steel account.',
        keywords: 'jenkins account',
        robots: 'noindex, nofollow'
    },
    {
        match: (path) => path.startsWith('/privacy-policy'),
        title: `Privacy Policy | ${SITE_NAME}`,
        description: 'Read the Jenkins Iron & Steel privacy policy.',
        keywords: 'privacy policy'
    },
    {
        match: (path) => path.startsWith('/404'),
        title: `Page Not Found | ${SITE_NAME}`,
        description: 'The page you requested could not be found.',
        keywords: '404',
        robots: 'noindex, nofollow'
    }
];

function upsertMeta(attrName, attrValue, content) {
    if (!content) {
        return;
    }

    let meta = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, attrValue);
        document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
}

function upsertCanonical(canonicalUrl) {
    if (!canonicalUrl) {
        return;
    }

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
}

function upsertJsonLd(id, payload) {
    if (!payload) {
        return;
    }

    let script = document.head.querySelector(`script[data-seo-jsonld="${id}"]`);
    if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo-jsonld', id);
        document.head.appendChild(script);
    }

    script.text = JSON.stringify(payload);
}

function toPrettyText(value) {
    if (!value) {
        return '';
    }

    return value
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

class SeoRouteManager extends Component {
    componentDidMount() {
        this.applySeo(this.props.location.pathname);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.applySeo(this.props.location.pathname);
        }
    }

    applySeo(pathname) {
        const currentPath = (pathname || '/').toLowerCase();
        const seoMatch = SEO_ROUTES.find((route) => route.match(currentPath)) || {};

        const title = seoMatch.title || DEFAULT_TITLE;
        const description = seoMatch.description || DEFAULT_DESCRIPTION;
        const keywords = seoMatch.keywords || DEFAULT_KEYWORDS;
        const robots = seoMatch.robots || 'index, follow';
        const ogType = seoMatch.ogType || 'website';
        const canonical = `${window.location.origin}${pathname || '/'}`;
        const urlSegments = currentPath.split('/').filter(Boolean);

        document.title = title;
        upsertCanonical(canonical);

        upsertMeta('name', 'description', description);
        upsertMeta('name', 'keywords', keywords);
        upsertMeta('name', 'robots', robots);

        upsertMeta('property', 'og:title', title);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:type', ogType);
        upsertMeta('property', 'og:url', canonical);
        upsertMeta('property', 'og:site_name', SITE_NAME);
        upsertMeta('property', 'og:image', DEFAULT_SOCIAL_IMAGE);
        upsertMeta('property', 'og:image:alt', SITE_NAME);

        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:title', title);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'twitter:image', DEFAULT_SOCIAL_IMAGE);

        const organizationSchema = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: SITE_NAME,
            url: 'https://jenkinsironandsteel.com/',
            logo: DEFAULT_SOCIAL_IMAGE,
            telephone: '+1-855-732-3843',
            sameAs: [
                'https://www.facebook.com/jenkinsironandsteel'
            ]
        };

        const websiteSchema = {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_NAME,
            url: 'https://jenkinsironandsteel.com/'
        };

        const breadcrumbItems = [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://jenkinsironandsteel.com/'
            }
        ];

        if (urlSegments.length > 0) {
            let builtPath = '';
            urlSegments.forEach((segment, index) => {
                builtPath += `/${segment}`;
                breadcrumbItems.push({
                    '@type': 'ListItem',
                    position: index + 2,
                    name: toPrettyText(segment),
                    item: `https://jenkinsironandsteel.com${builtPath}/`
                });
            });
        }

        const breadcrumbSchema = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems
        };

        upsertJsonLd('organization', organizationSchema);
        upsertJsonLd('website', websiteSchema);
        upsertJsonLd('breadcrumbs', breadcrumbSchema);

        if (currentPath.startsWith('/products/')) {
            const productSlug = urlSegments[1] || 'skid-steer-attachment';
            const productName = toPrettyText(productSlug) || 'Skid Steer Attachment';
            const productSchema = {
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: productName,
                brand: {
                    '@type': 'Brand',
                    name: SITE_NAME
                },
                description,
                url: canonical,
                category: 'Skid Steer Attachments'
            };

            upsertJsonLd('product', productSchema);
        } else {
            const productScript = document.head.querySelector('script[data-seo-jsonld="product"]');
            if (productScript) {
                productScript.remove();
            }
        }
    }

    render() {
        return null;
    }
}

export default withRouter(SeoRouteManager);
